import { useEffect } from "react";

import {
  getUserIdFromRefreshToken,
  isLoggedIn,
  RemoteConfigClient,
  RemoteConfigProvider,
  type RemoteConfigReactClient,
  Spinner,
} from "@shapr3d/frontend-lib";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { accountURL, PostMessages, remoteConfigEnvironmentId } from "../../config";
import { useLogout } from "../../hooks/useAuth";
import { AuthLayout } from "../../layouts/AuthLayout/AuthLayout";
import { LoadGTMLayout } from "../../layouts/LoadGTMLayout";
import { TeamsLayout } from "../../layouts/TeamsLayout/TeamsLayout";
import { trackingAdapter } from "../../lib/TrackingAdapter";
import AuthPage from "../../pages/AuthPage/AuthPage";
import ModelViewerDebugPage from "../../pages/ModelViewerDebugPage/ModelViewerDebugPage";
import ModelViewerPage from "../../pages/ModelViewerPage/ModelViewerPage";
import NotFoundPage from "../../pages/NotFoundPage/NotFoundPage";
import ProjectPage from "../../pages/ProjectPage/ProjectPage";
import LinkListPage from "../../pages/ViewLinkListPage/ViewLinkListPage";
import useTracker from "../../utils/useTracker";
import { IdentifyLoggedInUser } from "./components/IdentifyLoggedInUser";
import { TrackPageView } from "./components/TrackPageView";

const remoteConfigClient = new RemoteConfigClient({
  environmentID: remoteConfigEnvironmentId,
  identityID: trackingAdapter.getTrackingID(),
});

export let remoteConfigReactClient: RemoteConfigReactClient;

const Logout = () => {
  const { mutateAsync } = useLogout({ redirect: false, withRequest: true, withUMCLogout: false });
  useEffect(() => {
    (async () => {
      if (isLoggedIn()) {
        await mutateAsync();
      }
      top?.postMessage(PostMessages.LogoutFinished, { targetOrigin: accountURL });
    })();
  }, [mutateAsync]);
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Spinner />
      Logging out
    </div>
  );
};

export const TeamsFallback = () => {
  const location = useLocation();

  return <Navigate to={{ pathname: "/teams", search: location.search }} replace={true} />;
};

function App() {
  const tracker = useTracker();
  useEffect(() => {
    tracker.setUserData({ userID: getUserIdFromRefreshToken() });
  }, [tracker]);

  return (
    <RemoteConfigProvider
      remoteConfigClient={remoteConfigClient}
      ref={(client: RemoteConfigReactClient) => {
        remoteConfigReactClient = client;
      }}
    >
      <TrackPageView />
      <IdentifyLoggedInUser />
      <Routes>
        <Route element={<LoadGTMLayout />}>
          <Route path="/v/:viewLink">
            <Route index element={<ModelViewerPage />} />
            <Route path="drawings/:drawingId" element={<ModelViewerPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route path="/p/:projectId">
            <Route index element={<ProjectPage />} />
            <Route path="*" element={<NotFoundPage isProjectPage />} />
          </Route>
          <Route path="/auth">
            <Route path="" element={<AuthPage />} />
            <Route path="logout" element={<Logout />} />
          </Route>
          <Route element={<AuthLayout />}>
            <Route path="teams" element={<TeamsLayout />}>
              <Route path=":teamID">
                <Route path="review-links?" element={<LinkListPage />} />
                <Route path="*" element={<TeamsFallback />} />
              </Route>
            </Route>
          </Route>
          <Route path="/debug" element={<ModelViewerDebugPage />} />
          <Route path="*" element={<TeamsFallback />} />
        </Route>
      </Routes>
    </RemoteConfigProvider>
  );
}

export default App;
